import { Card, Button, CircularProgress, Snackbar, Alert } from "@mui/material";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import { useStyles } from "./ActionPlanComments.styles";
import MainCardButtonAction from "components/MainCardButtonAction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RCACommentItem from "pages/Corporate/RCA/components/RCACommentItem";
import { useTranslation } from "react-i18next";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import { useState, useCallback, useEffect, useRef, useContext, useMemo } from "react";
import ActionPlanCommentModal from "./ActionPlanCommentModal";
import { IActionPlanComment } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import { IActionPlanCommentsProps } from "./ActionPlanCommentsInterfaces";

export const COMMENTS_PER_PAGE_ACTION_PLAN_LIMIT = 5;
const SNACKBAR_DURATION = 6000;
const SNACKBAR_ANCHOR = { vertical: "top" as const, horizontal: "right" as const };
const TRANSLATION_PREFIX = "rca.action_plans.action_plan_details.comments";

const filterDeletedComments = (comments: IActionPlanComment[], deletedIds: Set<number>): IActionPlanComment[] => {
    return comments.filter((comment) => !deletedIds.has(comment.action_plan_comment_id));
};

const ActionPlanComments = ({ actionPlanId, canEdit = false }: IActionPlanCommentsProps) => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    // Modal state
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [selectedComment, setSelectedComment] = useState<IActionPlanComment | null>(null);

    // Comments state
    const [loadedComments, setLoadedComments] = useState<IActionPlanComment[]>([]);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [deletedCommentIds, setDeletedCommentIds] = useState<Set<number>>(new Set());
    const [adjustedTotalCount, setAdjustedTotalCount] = useState<number>(0);

    // Snackbar state
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarFailureMessage, setSnackbarFailureMessage] = useState("");

    // Delete modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<IActionPlanComment | null>(null);

    // Refs
    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const hasScrolledToBottomRef = useRef(false);

    const { actionPlanComments, loading, refetch, refetchTotalCount, totalCount } =
        ActionPlanEntity.GetActionPlanComments(actionPlanId);
    const { updateActionPlanComment } = ActionPlanEntity.UpdateActionPlanComment();

    useEffect(() => {
        if (totalCount > 0) {
            setAdjustedTotalCount(totalCount);
        }
    }, [totalCount]);

    useEffect(() => {
        if (actionPlanComments.length > 0 && loadedComments.length === 0) {
            const filteredComments = filterDeletedComments(actionPlanComments, deletedCommentIds);
            if (filteredComments.length > 0) {
                const reversedComments = [...filteredComments].reverse();
                setLoadedComments(reversedComments);
                setCurrentOffset(filteredComments.length);
                hasScrolledToBottomRef.current = false;
            }
        }
    }, [actionPlanComments, loadedComments.length, deletedCommentIds]);

    const handleEditComplete = async (updatedDescription: string) => {
        if (!selectedComment) return;

        setLoadedComments((prev) =>
            prev.map((comment) =>
                comment.action_plan_comment_id === selectedComment.action_plan_comment_id
                    ? { ...comment, description: updatedDescription, updated_at: new Date() }
                    : comment,
            ),
        );
        setSnackbarMessage(t(`${TRANSLATION_PREFIX}.edit_success`));
    };

    const handleAddComplete = async () => {
        setLoadedComments([]);
        setCurrentOffset(0);
        hasScrolledToBottomRef.current = false;

        try {
            await refetchTotalCount();

            const result = await refetch({
                actionPlanId,
                limit: COMMENTS_PER_PAGE_ACTION_PLAN_LIMIT,
                offset: 0,
                order_by: {
                    created_at: "desc",
                },
            });

            if (result?.data?.action_plan_comment) {
                const comments: IActionPlanComment[] = result.data.action_plan_comment;
                const filteredComments = filterDeletedComments(comments, deletedCommentIds);
                if (filteredComments.length > 0) {
                    const reversedComments = [...filteredComments].reverse();
                    setLoadedComments(reversedComments);
                    setCurrentOffset(filteredComments.length);
                    hasScrolledToBottomRef.current = false;
                }
            }

            setSnackbarMessage(t(`${TRANSLATION_PREFIX}.add_success`));
        } catch (error) {
            console.error("Error refetching comments:", error);
            setSnackbarFailureMessage(t(`${TRANSLATION_PREFIX}.add_failed`));
        }
    };

    const handleCommentComplete = async (updatedDescription?: string) => {
        if (updatedDescription && selectedComment) {
            await handleEditComplete(updatedDescription);
        } else {
            await handleAddComplete();
        }
    };

    const handleCommentError = () => {
        const errorKey = selectedComment ? "edit_failed" : "add_failed";
        setSnackbarFailureMessage(t(`${TRANSLATION_PREFIX}.${errorKey}`));
    };

    const handleAddComment = () => {
        setSelectedComment(null);
        setIsCommentModalOpen(true);
    };

    const handleEditComment = (comment: IActionPlanComment) => {
        setSelectedComment(comment);
        setIsCommentModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsCommentModalOpen(false);
        setSelectedComment(null);
    };

    const handleDeleteComment = (comment: IActionPlanComment) => {
        setCommentToDelete(comment);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!commentToDelete) return;

        const deletedId = commentToDelete.action_plan_comment_id;

        try {
            await updateActionPlanComment({
                variables: {
                    actionPlanCommentId: deletedId,
                    changes: {
                        deleted_at: new Date(),
                    },
                },
            });

            setDeletedCommentIds((prev) => new Set(prev).add(deletedId));

            setLoadedComments((prev) => prev.filter((comment) => comment.action_plan_comment_id !== deletedId));

            setAdjustedTotalCount((prev) => Math.max(0, prev - 1));

            setSnackbarMessage(t(`${TRANSLATION_PREFIX}.delete_success`));
            setIsDeleteModalOpen(false);
            setCommentToDelete(null);
        } catch (error) {
            setSnackbarFailureMessage(t(`${TRANSLATION_PREFIX}.delete_failed`));
            setIsDeleteModalOpen(false);
            setCommentToDelete(null);
        }
    };

    const handleShowMoreComments = useCallback(async () => {
        setIsLoadingMore(true);
        const nextOffset = currentOffset;

        try {
            const result = await refetch({
                actionPlanId,
                limit: COMMENTS_PER_PAGE_ACTION_PLAN_LIMIT,
                offset: nextOffset,
                order_by: {
                    created_at: "desc",
                },
            });

            if (result?.data?.action_plan_comment) {
                const newComments: IActionPlanComment[] = result.data.action_plan_comment;
                const filteredNewComments = filterDeletedComments(newComments, deletedCommentIds);
                if (filteredNewComments.length > 0) {
                    const reversedNewComments = [...filteredNewComments].reverse();
                    setLoadedComments((prev) => [...reversedNewComments, ...prev]);
                    setCurrentOffset(nextOffset + filteredNewComments.length);
                } else if (newComments.length > 0) {
                    setCurrentOffset(nextOffset + newComments.length);
                }
            }
        } catch (error) {
            console.error("Error loading more comments:", error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [actionPlanId, refetch, currentOffset, deletedCommentIds]);

    const filteredActionPlanComments = useMemo(
        () => filterDeletedComments(actionPlanComments, deletedCommentIds),
        [actionPlanComments, deletedCommentIds],
    );

    const displayComments = useMemo(() => {
        if (loadedComments.length > 0) {
            return loadedComments;
        }
        return [...filteredActionPlanComments].reverse();
    }, [loadedComments, filteredActionPlanComments]);

    const hasMoreComments = useMemo(
        () => loadedComments.length < adjustedTotalCount,
        [loadedComments.length, adjustedTotalCount],
    );

    useEffect(() => {
        if (displayComments.length > 0 && commentsContainerRef.current && !hasScrolledToBottomRef.current) {
            commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
            hasScrolledToBottomRef.current = true;
        }
    }, [displayComments.length]);

    return (
        <Card className={`${mainClasses.contentCard} ${classes.card}`}>
            <div className={mainClasses.contentCardHeader}>
                <div className={mainClasses.contentCardTitle}>
                    <span>{t(`${TRANSLATION_PREFIX}.title`)}</span>
                </div>
                <div className={mainClasses.contentCardActions}>
                    <MainCardButtonAction
                        disabled={!canEdit}
                        title={t(`${TRANSLATION_PREFIX}.add_new_comment`)}
                        renderIcon={() => <AddCircleIcon />}
                        onClick={handleAddComment}
                    />
                </div>
            </div>
            <div ref={commentsContainerRef} className={classes.cardContentComments}>
                {loading && loadedComments.length === 0 ? (
                    <CircularProgress size={20} color="info" />
                ) : displayComments.length === 0 ? (
                    <div>{t(`${TRANSLATION_PREFIX}.no_comments`)}</div>
                ) : (
                    <>
                        {hasMoreComments && (
                            <div className={classes.showMoreWrapper}>
                                {isLoadingMore ? (
                                    <CircularProgress size={20} color="info" />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={handleShowMoreComments}
                                        disabled={isLoadingMore}
                                        className={classes.showMoreButton}
                                    >
                                        {t(`${TRANSLATION_PREFIX}.show_more_comments`)}
                                    </Button>
                                )}
                            </div>
                        )}
                        {displayComments.map((comment) => (
                            <RCACommentItem
                                comment={comment}
                                key={comment.action_plan_comment_id}
                                currentUserId={currentUser?.user_id}
                                onEdit={handleEditComment}
                                onDelete={handleDeleteComment}
                            />
                        ))}
                    </>
                )}
            </div>
            <ActionPlanCommentModal
                open={isCommentModalOpen}
                onClose={handleCloseModal}
                onComplete={handleCommentComplete}
                onError={handleCommentError}
                actionPlanId={actionPlanId}
                comment={
                    selectedComment
                        ? {
                              action_plan_comment_id: selectedComment.action_plan_comment_id,
                              description: selectedComment.description,
                          }
                        : undefined
                }
            />
            {/* Success Snackbar */}
            <Snackbar
                open={!!snackbarMessage}
                onClose={() => setSnackbarMessage("")}
                autoHideDuration={SNACKBAR_DURATION}
                anchorOrigin={SNACKBAR_ANCHOR}
            >
                <Alert severity="success" sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            {/* Error Snackbar */}
            <Snackbar
                open={!!snackbarFailureMessage}
                onClose={() => setSnackbarFailureMessage("")}
                autoHideDuration={SNACKBAR_DURATION}
                anchorOrigin={SNACKBAR_ANCHOR}
            >
                <Alert severity="error" sx={{ width: "100%" }}>
                    {snackbarFailureMessage}
                </Alert>
            </Snackbar>
            <RCAConfirmDeleteModal
                open={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setCommentToDelete(null);
                }}
                onDeleteItem={handleConfirmDelete}
                title={t(`${TRANSLATION_PREFIX}.delete_comment_modal.title`)}
                warning={t(`${TRANSLATION_PREFIX}.delete_comment_modal.warning`)}
                confirm={t(`${TRANSLATION_PREFIX}.delete_comment_modal.confirm`)}
            />
        </Card>
    );
};

export default ActionPlanComments;
